// ForceChangePasswordScreen — rendered when the authenticated user has
// must_change_password=true. Two modes:
//   - capturedPassword provided: New + Confirm fields only (post-login path)
//   - capturedPassword undefined: Current + New + Confirm fields (orphan path)
//
// On success, calls onSuccess({ token, user }) where user.must_change_password
// is locally set to false. The JWT is unchanged; only the server-side flag
// is cleared. The parent is responsible for advancing the flow (2FA setup
// if needed, otherwise dashboard).

import { useState } from "react";
import { T } from "../theme";
import { Btn, Inp, Card } from "./primitives";

type Props = {
  user: { id: string; email: string; name: string; role: string };
  token: string;
  capturedPassword?: string | null;
  onSuccess: (updated: { token: string; user: any }) => void;
  onLogout?: () => void;
};

export const ForceChangePasswordScreen = ({
  user,
  token,
  capturedPassword,
  onSuccess,
  onLogout,
}: Props) => {
  const [currentPw, setCurrentPw] = useState<string>("");
  const [newPw, setNewPw] = useState<string>("");
  const [confirmPw, setConfirmPw] = useState<string>("");
  const [errors, setErrors] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const needsCurrentField = !capturedPassword;

  const submit = async () => {
    setErrors([]);
    const localErrors: string[] = [];
    const effectiveCurrent = capturedPassword || currentPw;
    if (!effectiveCurrent) localErrors.push("Current password is required.");
    if (!newPw) localErrors.push("New password is required.");
    if (newPw && newPw !== confirmPw) localErrors.push("New password and confirmation do not match.");
    if (newPw && effectiveCurrent && newPw === effectiveCurrent) {
      localErrors.push("New password must differ from current password.");
    }
    if (localErrors.length) { setErrors(localErrors); return; }

    setSubmitting(true);
    try {
      const res = await fetch("/api/auth/password", {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify({ currentPassword: effectiveCurrent, newPassword: newPw }),
      });
      const data = await res.json();

      if (res.ok) {
        try {
          const stored = JSON.parse(localStorage.getItem("kontractai_user") || "{}");
          const updated = { ...stored, must_change_password: false, mustChangePassword: false };
          localStorage.setItem("kontractai_user", JSON.stringify(updated));
          onSuccess({ token, user: updated });
        } catch {
          onSuccess({ token, user: { ...user, must_change_password: false } });
        }
        return;
      }

      if (data.error === "password_policy_violation" && Array.isArray(data.errors)) {
        setErrors(data.errors);
      } else if (data.error) {
        setErrors([String(data.error)]);
      } else {
        setErrors(["Password change failed. Please try again."]);
      }
    } catch (err: any) {
      setErrors(["Connection error. Please try again."]);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-deploy-marker="wave3-force-change-password"
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "#F5F7FA",
        fontFamily: "'Outfit', 'Segoe UI', sans-serif",
      }}
    >
      <Card
        style={{
          maxWidth: 440,
          width: "100%",
          padding: 32,
          textAlign: "left",
          boxShadow: "0 2px 12px rgba(0,0,0,.06)",
        }}
      >
        <div style={{ textAlign: "center", marginBottom: 20 }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: T.tx, marginBottom: 4 }}>
            Set a new password
          </div>
          <div style={{ fontSize: 12, color: T.txM }}>
            Your administrator requires you to set a new password before continuing.
          </div>
        </div>

        <div style={{ fontSize: 11, color: T.txD, marginBottom: 16, textAlign: "center" }}>
          Signed in as <strong>{user.email}</strong>
        </div>

        {needsCurrentField && (
          <Inp
            label="Current Password"
            value={currentPw}
            onChange={setCurrentPw}
            type="password"
            placeholder="Enter your current password"
          />
        )}

        <Inp
          label="New Password"
          value={newPw}
          onChange={setNewPw}
          type="password"
          placeholder="Choose a new password"
        />
        <Inp
          label="Confirm New Password"
          value={confirmPw}
          onChange={setConfirmPw}
          type="password"
          placeholder="Re-enter the new password"
        />

        {errors.length > 0 && (
          <div
            style={{
              fontSize: 11,
              color: T.red,
              backgroundColor: T.redS,
              padding: "8px 10px",
              borderRadius: 4,
              marginBottom: 12,
            }}
          >
            <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.5 }}>
              {errors.map((e, i) => (
                <li key={i}>{e}</li>
              ))}
            </ul>
          </div>
        )}

        <Btn
          v="primary"
          sz="lg"
          full
          onClick={() => { submit(); }}
          disabled={submitting || !newPw || !confirmPw || (needsCurrentField && !currentPw)}
        >
          {submitting ? "Updating..." : "Update password and continue"}
        </Btn>

        {onLogout && (
          <div style={{ textAlign: "center", marginTop: 16 }}>
            <button
              onClick={onLogout}
              style={{
                background: "none",
                border: "none",
                color: T.txD,
                cursor: "pointer",
                fontSize: 11,
                fontFamily: "inherit",
              }}
            >
              Sign out instead
            </button>
          </div>
        )}
      </Card>
    </div>
  );
};
